import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'

import { safeStorage } from '@/state/storage'
import { Exchange } from '@/types/generated/common/types'

export type WidgetAction =
  | { type: 'close' | 'duplicate' | 'move-to-top' | 'minimize'; cardId: string }
  | { type: 'clear-all' }
  | null
export const widgetActionAtom = atom<WidgetAction>(null)

// Mobile: collapsed state per widget instance id
export const mobileCollapsedAtom = atomWithStorage<Record<string, boolean>>(
  `dashboard-mobile-collapsed-${process.env.NEXT_PUBLIC_ENV}`,
  {},
  safeStorage,
  { getOnInit: true }
)

// Mobile: signals which widget's column settings sheet is open (null = closed)
export const mobileColumnSettingsCardIdAtom = atom<string | null>(null)

// Mobile: signals which widget's filter settings sheet is open (null = closed)
export const mobileFilterSettingsCardIdAtom = atom<string | null>(null)

// Global: true while the hedge builder dialog is open. Hedges aren't scoped
// to a specific widget instance (the dialog reads positionsAtom directly —
// see useHedgeBuilder), so every entry point (order form type dropdown,
// Active tab's Hedges section header, mobile overflow menu) shares this one
// atom rather than routing through a cardId like the atoms above.
export const hedgeDialogOpenAtom = atom(false)

// Sidebar navigation presents the hedge builder as a full-page tool, while contextual
// trading actions continue to use the regular dialog size.
export const hedgeDialogFullscreenAtom = atom(false)

export interface HedgeDialogPreset {
  accountId: string
  exchange: Exchange
  symbol?: string
}

/** A position picked in a full-page tool's own positions table (Delta Hedge, Arbitrage): the
 *  tool's builder retargets to it every time — `id` makes a repeat pick of the same row land
 *  again. */
export interface PositionSelectRequest {
  id: string
  accountId: string
  exchange: Exchange
  symbol: string
}
export type HedgePositionSelectRequest = PositionSelectRequest

// Account filter or position identity supplied by contextual entry points. The builder resolves the
// actual underlying from the live position/instrument stores instead of guessing from a symbol.
export const hedgeDialogPresetAtom = atom<HedgeDialogPreset | null>(null)

// Global: true while the cross-exchange arb builder dialog is open.
export const arbDialogOpenAtom = atom(false)

export interface ArbDialogLegSeed {
  accountId: string
  symbol?: string
}

// Optional leg prefill supplied by contextual entry points (e.g. the order form's
// Arb tab, seeded from whatever instruments are currently selected there) — mirrors
// hedgeDialogPresetAtom's pattern. Account-filter entry points may seed only an account.
export const arbDialogSeedAtom = atom<{
  leg1?: ArbDialogLegSeed
  leg2?: ArbDialogLegSeed
} | null>(null)
